import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Header from './Header'
import Main from './Main'
import Footer from './Footer'
import DocLibFilter from './DocLibFilter'
import { makeStyles } from '@fluentui/react-components'

const useStyles = makeStyles({
  root: {
    minHeight: '100vh'
  },
  stickyHeaderContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 100, // Ensure it's above the content
    backgroundColor: 'white' // Prevent content from showing through during scroll
  }
})

/**
 * ChatInterface Component
 *
 * The primary layout wrapper for the application. It orchestrates the positioning
 * and state distribution among the top-level sections: Header, Data Source Filters (DocLibFilter),
 * Main chat area, and the Footer input area.
 *
 * It also manages the auto-scrolling behavior of the main chat window.
 *
 * @param {Object} props - The component properties.
 * @param {string} props.title - Application title.
 * @param {Function} props.onSourceChange - Handler for when the user switches data sources.
 * @param {boolean} props.isCheckingFiles - Flag indicating if files are currently being validated.
 * @param {Function} props.onUploadClick - Handler to open the file upload modal.
 * @param {string} props.activeButton - The currently active data source (e.g., 'LLM', 'file_upload').
 * @param {Object} props.user - The currently authenticated user.
 * @param {Function} props.onLogout - Handler for logging out.
 * @param {Array} props.messages - Array of chat message objects to be rendered.
 * @param {Function} props.onSendMessage - Handler to send a new message.
 * @param {string} props.error - Global error message to display.
 * @param {boolean} props.isLoading - Global loading state.
 * @param {Function} props.onClearChat - Handler to clear the chat history.
 * @param {Function} props.onShowInfo - Handler to show app info.
 * @param {Function} props.onShowPromptLibrary - Handler to open the prompt library.
 * @param {Array} props.existingFiles - List of files currently available/uploaded.
 * @param {Function} props.onToggleFileSelection - Handler to toggle individual file selection.
 * @param {Function} props.onSelectAll - Handler to toggle all files selection.
 * @param {Function} props.onFeedbackSubmit - Handler to submit like/dislike feedback.
 * @param {string} props.sessionId - The current session ID.
 * @param {Function} props.onLogCopy - Handler to log when a user copies a response.
 * @param {Function} props.onToggleBookmark - Handler to toggle bookmarking a response.
 * @param {boolean} props.isUploading - Flag indicating if a file upload is in progress.
 * @param {boolean} props.showRevaluationWarning - Flag to show file re-evaluation warning.
 * @param {Function} props.onDismissRevaluationWarning - Handler to dismiss the re-evaluation warning.
 * @param {Object} props.docLibFilters - Active filters for the DocLib data source.
 * @param {Function} props.onDocLibApply - Handler to apply new DocLib filters.
 * @param {Function} props.onRemoveDocLibFilter - Handler to remove a specific DocLib filter.
 * @param {Function} props.onRetry - Handler to retry a failed action.
 * @param {boolean} props.isGroundingEnabled - Flag indicating if Google Grounding is enabled.
 * @param {Function} props.onGroundingChange - Handler to toggle Google Grounding.
 * @param {boolean} props.isGenerating - Flag indicating if the LLM is currently generating a response.
 * @param {Function} props.onStopGenerating - Handler to abort LLM generation.
 * @param {boolean} props.isStopping - Flag indicating if the stop generation request is processing.
 */
const ChatInterface = ({
  title,
  onSourceChange,
  isCheckingFiles,
  onUploadClick,
  activeButton,
  user,
  onLogout,
  messages,
  onSendMessage,
  error,
  isLoading,
  onClearChat,
  onShowInfo,
  onShowPromptLibrary,
  existingFiles,
  onToggleFileSelection,
  onSelectAll,
  onFeedbackSubmit,
  sessionId,
  onLogCopy,
  onToggleBookmark,
  isUploading,
  showRevaluationWarning,
  onDismissRevaluationWarning,
  docLibFilters,
  onDocLibApply,
  onRemoveDocLibFilter,
  onRetry,
  isGroundingEnabled,
  onGroundingChange,
  isGenerating,
  onStopGenerating,
  isStopping,
  romineRequiredRole,   
}) => {
  const styles = useStyles()

  // A ref to hold the DOM node of our scrollable chat container.
  const chatContainerRef = useRef(null)

  /**
  * Effect Hook: Auto-scrolls the chat container when the messages array changes.
  * - Forces scroll to the bottom if the user just sent a message or if the bot is "typing".
  * - Uses "smart scrolling" (only auto-scrolls if already near the bottom) if the bot
  *   is streaming a long response, preventing the UI from aggressively stealing control
  *   if the user has scrolled up to read past history.
  */
  useEffect(
    () => {
      const container = chatContainerRef.current
      if (!container) return
      const lastMessage = messages[messages.length - 1]
      if (!lastMessage) return
      // We will FORCE the scroll to the bottom if the user just sent a message.
      // We can detect this event by checking if the last message is either from
      // the user OR is the special 'typing' indicator that immediately follows.
      if (lastMessage.sender === 'user' || lastMessage.type === 'typing') {
        container.scrollTop = container.scrollHeight
        return
      }

      const { scrollTop, scrollHeight, clientHeight } = container
      const scrollThreshold = 100
      const userScrollPositionFromBottom =
        scrollHeight - scrollTop - clientHeight

      if (userScrollPositionFromBottom <= scrollThreshold) {
        container.scrollTop = container.scrollHeight
      }
    },
    [messages]
  )

  return (
    <div className={`${styles.root} d-flex flex-column h-100`}>
      <header className='mb-0'>
        <Header
          title={title}
          onSourceChange={onSourceChange}
          onUploadClick={onUploadClick}
          isUploadDisabled={isCheckingFiles}
          isUploading={isUploading}
          activeButton={activeButton}
          user={user}
          onLogin={() => { }}
          onLogout={onLogout}
          onClearChat={onClearChat}
          onShowInfo={onShowInfo}
          onShowPromptLibrary={onShowPromptLibrary}
          isLoading={isLoading}
          docLibFilters={docLibFilters}
          onDocLibApply={onDocLibApply}
            romineRequiredRole={romineRequiredRole}
        />
      </header>
      {/* CONDITIONAL RENDERING OF THE NEW FILTER PANEL */}
      {activeButton === 'DocLib' &&
        <DocLibFilter
          initialFilters={docLibFilters}
          onApply={onDocLibApply}
          sessionId={sessionId}
          disabled={isGenerating || isLoading}
        />}
            
      <main
        className='flex-shrink-0'
        ref={chatContainerRef}
        style={{
          height: '15vh',
          overflowY: 'auto',
          position: 'relative',
          zIndex: 1,
          flexGrow: 1,
          overflowX: 'hidden'
        }}
      >
        <Main
          messages={messages}
          isLoading={isLoading}
          uploadedFileNames={existingFiles.map(f => f.name)}
          onFeedbackSubmit={onFeedbackSubmit}
          sessionId={sessionId}
          onLogCopy={onLogCopy}
          onToggleBookmark={onToggleBookmark}
          onRetry={onRetry}
        />
      </main>
          
      {/* CONDITIONAL FOOTER: Hides the input box entirely when in RoMine mode */}
      {activeButton !== 'RoMine' && (
      <footer className='footer mt-auto'>
        <Footer
          onSendMessage={onSendMessage}
          error={error}
          isLoading={isLoading}
          activeButton={activeButton}
          files={existingFiles}
          onToggleFileSelection={onToggleFileSelection}
          onSelectAll={onSelectAll}
          isUploading={isUploading}
          showRevaluationWarning={showRevaluationWarning}
          onDismissRevaluationWarning={onDismissRevaluationWarning}
          docLibFilters={docLibFilters}
          onRemoveDocLibFilter={onRemoveDocLibFilter}
          isGroundingEnabled={isGroundingEnabled}
          onGroundingChange={onGroundingChange}
          isGenerating={isGenerating}
          onStopGenerating={onStopGenerating}
          isStopping={isStopping} 
        />
      </footer>
      )}
    </div>
  )
}

ChatInterface.propTypes = {
  title: PropTypes.string,
  onSourceChange: PropTypes.func.isRequired,
  onUploadClick: PropTypes.func.isRequired,
  isCheckingFiles: PropTypes.bool,
  activeButton: PropTypes.string,
  user: PropTypes.object,
  onLogout: PropTypes.func.isRequired,
  messages: PropTypes.array.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  onClearChat: PropTypes.func.isRequired,
  onShowInfo: PropTypes.func.isRequired,
  onShowPromptLibrary: PropTypes.func.isRequired,
  onFeedbackSubmit: PropTypes.func.isRequired,
  sessionId: PropTypes.string.isRequired,
  onLogCopy: PropTypes.func.isRequired,
  onToggleBookmark: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  existingFiles: PropTypes.arrayOf(PropTypes.object),
  onToggleFileSelection: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  showRevaluationWarning: PropTypes.bool,
  onDismissRevaluationWarning: PropTypes.func,
  docLibFilters: PropTypes.object,
  onDocLibApply: PropTypes.func,
  onRemoveDocLibFilter: PropTypes.func,
  onRetry: PropTypes.func,
  isGroundingEnabled: PropTypes.bool,
  onGroundingChange: PropTypes.func,
  isGenerating: PropTypes.bool,
  onStopGenerating: PropTypes.func,
  isStopping: PropTypes.bool,
  romineRequiredRole: PropTypes.arrayOf(PropTypes.string),
}

ChatInterface.defaultProps = {
  title: '',
  isCheckingFiles: false,
  activeButton: 'LLM',
  user: null,
  error: null,
  isLoading: false,
  isUploading: false,
  existingFiles: [],
  showRevaluationWarning: false,
  onDismissRevaluationWarning: undefined,
  docLibFilters: null,
  onDocLibApply: undefined,
  onRemoveDocLibFilter: undefined,
  onRetry: undefined,
  isGroundingEnabled: false,
  onGroundingChange: undefined,
  isGenerating: false,
  onStopGenerating: undefined,
  isStopping: false
}
export default ChatInterface
